<template>
  <v-card
    border
    class="d-flex flex-column"
    :style="cssProps"
  >
    <v-card-title class="d-flex align-center justify-space-between pa-4">
      <h5>{{ $t('leads.master.move-contacts.title') }}</h5>
      <v-btn
        icon="mdi-close"
        variant="text"
        class="text-gray"
        @click="close"
      />
    </v-card-title>

    <v-card-text>
      <p>
        {{ $t('leads.master.move-contacts.subtitle') }}
      </p>

      <div v-if="hasApplicableLeads">
        <div class="mb-2 rounded-lg mx-n2 finished-step mt-4 pa-2">
          <div class="d-flex">
            <v-icon
              :class="{ blink: isStepContactFilterFinished }"
              size="small"
            >
              mdi-arrow-right
            </v-icon>
            <h4 :class="{ blink: isStepContactFilterFinished }">
              {{ $t('leads.master.move-contacts.step-one') }}
            </h4>
            <v-icon
              v-if="isStepContactFilterFinished"
              class="ml-2"
              size="small"
              color="primary"
            >
              mdi-check-circle
            </v-icon>
          </div>

          <p>{{ $t('leads.master.move-contacts.bulk.title') }}</p>
          <v-select
            v-model="contactFilter"
            class="mt-2 mb-2"
            variant="outlined"
            validate-on="blur"
            :items="contactFilters"
            item-title="text"
            item-value="value"
            :placeholder="$t('leads.master.move-contacts.select-contact')"
            hide-details
            density="compact"
            required
            :disabled="isLoading"
          />

          <slot name="selectAll" />
        </div>

        <div class="mb-2 rounded-lg mx-n2 finished-step mt-4 pa-2">
          <div class="d-flex">
            <v-icon
              :class="{ blink: isStepSelectedChildCompanyFinished }"
              size="small"
            >
              mdi-arrow-right
            </v-icon>
            <h4 :class="{ blink: isStepSelectedChildCompanyFinished }">
              {{ $t('leads.master.move-contacts.step-two') }}
            </h4>
            <v-icon
              v-if="isStepSelectedChildCompanyFinished"
              class="ml-2"
              size="small"
              color="primary"
            >
              mdi-check-circle
            </v-icon>
          </div>
          <v-select
            v-model="selectedChildCompany"
            :items="childCompanies"
            :disabled="
              childCompaniesLoading ||
              !childCompanies ||
              childCompanies.length === 0 ||
              selectedLeadIds.length === 0 ||
              isLoading
            "
            class="mt-2 mb-2"
            variant="outlined"
            validate-on="blur"
            item-title="text"
            item-value="value"
            :placeholder="$t('leads.master.move-contacts.select-company')"
            hide-details
            density="compact"
            required
          />
        </div>

        <div class="mt-4 mb-2 rounded-lg mx-n2 pa-2">
          <div class="d-flex">
            <v-icon
              v-if="isStepSubmit"
              :class="{ blink: isStepSubmit }"
              size="small"
            >
              mdi-arrow-right
            </v-icon>
            <h4 :class="{ blink: isStepSubmit }">
              {{ $t('leads.master.move-contacts.step-three') }}
            </h4>
          </div>

          <p>
            {{ $t('leads.master.move-contacts.info') }}
          </p>

          <MoveContactsButton
            :lead-ids="selectedLeadIds"
            :selected-child-company="selectedChildCompany"
            :contact-filter="contactFilter"
            @contactsMoved="contactsMoved"
            @isLoading="((isLoading = $event), $emit('isLoading', $event))"
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useDisplay, useTheme } from 'vuetify'

import featureMixin from '@/mixins/feature'
import GET_CHILD_COMPANIES from '@/modules/company/queries/ChildCompanies.gql'
import {
  InvitationContactFilter,
  ReadableMoveContactFilter,
} from '@/modules/leads/enums/Invitation.js'
import MoveContactsButton from '@/modules/master/leads/moveContactsButton.vue'

// Props
type Props = {
  selectedLeadIds: string[]
  hasCrm: boolean
  crmName: string
  activeChildCompany: { id?: string; name?: string }
  hasApplicableLeads: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selectedLeadIds: () => [],
  hasCrm: false,
  crmName: '',
  activeChildCompany: () => ({}),
  hasApplicableLeads: false,
})

// Emits
const emit = defineEmits(['updateFetchIdsParameters', 'close', 'contactsMoved', 'isLoading'])

// Access global mixins and vuetify
const instance = getCurrentInstance()
const isFeatureActive = featureMixin.methods.isFeatureActive.bind(instance.proxy)
const $t = instance?.proxy?.$t

const theme = useTheme()
const primaryColor = theme.current.value.colors.primary
const { smAndDown } = useDisplay()

// Reactive State (data())
const isLoading = ref(false)
const contactFilter = ref(null)
const childCompanies = ref([])
const childCompaniesLoading = ref(true)
const selectedChildCompany = ref(null)

// Apollo Query (childCompanies)
const { result: childCompaniesResult } = useQuery(GET_CHILD_COMPANIES)

watch(childCompaniesResult, (data) => {
  if (!data || !data.childCompanies) {
    childCompanies.value = []
    childCompaniesLoading.value = false
    return
  }

  childCompanies.value = data.childCompanies
    .filter((company) => company.id !== props.activeChildCompany?.id)
    .map((company) => ({
      text: company.name,
      value: company.id,
    }))

  childCompaniesLoading.value = false
})

// Computed properties
const contactFilters = computed(() => {
  const hasValuation = isFeatureActive?.('lp-immobilienbewertung')
  const hasPropertyRequest = isFeatureActive?.('lp-wohntraum-finder')
  const readableInvitationLabels = ReadableMoveContactFilter

  const readableMoveContactFilter = Object.entries(readableInvitationLabels)
    .map(([key, value]) => ({ value: key, text: $t(value) }))
    .filter(({ value }) => value !== InvitationContactFilter.VALUATIONS || hasValuation)
    .filter(({ value }) => value !== InvitationContactFilter.PROPERTY_REQUEST || hasPropertyRequest)

  if (props.hasCrm) {
    const crmFilter = {
      value: InvitationContactFilter.CRM,
      text: $t('labels.acquisition-booster.move-contact-filter.all-from-crm', {
        crm: props.crmName,
      }),
    }
    readableMoveContactFilter.splice(readableMoveContactFilter.length - 1, 0, crmFilter)
  }

  if (smAndDown.value) {
    return readableMoveContactFilter.filter(
      (filter) => filter.value !== InvitationContactFilter.CUSTOM
    )
  }
  return readableMoveContactFilter
})

const cssProps = computed(() => ({
  '--primary': primaryColor,
  '--primary-light': `${primaryColor}55`,
}))

const isStepContactFilterFinished = computed(
  () => contactFilter.value && props.selectedLeadIds.length > 0
)

const isStepSelectedChildCompanyFinished = computed(() => selectedChildCompany.value)

const isStepSubmit = computed(() => props.selectedLeadIds.length > 0 && contactFilter.value)

// Watchers
watch(contactFilter, () => {
  emit('updateFetchIdsParameters', {
    contactFilter: contactFilter.value,
    showFilter: contactFilter.value === InvitationContactFilter.CUSTOM,
  })
})

// Methods
function contactsMoved() {
  contactFilter.value = null
  selectedChildCompany.value = null
  emit('contactsMoved')
  close()
}

function close() {
  emit('close')
}
</script>

<style scoped>
.blink {
  animation: blink-animation 4s linear infinite;
  -webkit-animation: blink-animation 4s linear infinite;
}

.-mt-4 {
  margin-top: -4px;
}

@keyframes blink-animation {
  0% {
    color: rgba(0, 0, 0, 0.6);
  }

  50% {
    color: var(--primary);
  }

  100% {
    color: rgba(0, 0, 0, 0.6);
  }
}
</style>
